import { adminCaptureException } from '@diamond/shared/utils';
import { useAuthStore } from '@diamond/sol-admin/features/auth';
import { ProductRewardsResponse } from '@diamond/sol-buyer/utils';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import { getProductRewards } from '../api/get-product-rewards';

export const PRODUCT_REWARDS_QUERY_KEY = 'product-rewards';
type ApiErrorPayload = {
  message?: string;
  statusCode?: number;
  [key: string]: unknown;
};

const getValidStatusCode = (value: unknown): number | undefined => {
  if (typeof value === 'number') {
    return value;
  }

  const parsedStatus = Number(value);
  if (Number.isFinite(parsedStatus)) {
    return parsedStatus;
  }

  return undefined;
};

export const useProductRewards = (orderId: string | null) => {
  const session = useAuthStore();

  return useQuery<ProductRewardsResponse>({
    queryKey: [PRODUCT_REWARDS_QUERY_KEY, orderId],
    queryFn: async () => {
      try {
        return await getProductRewards(orderId!, session.access_token);
      } catch (error: unknown) {
        if (isAxiosError<ApiErrorPayload>(error)) {
          const axiosError = error;
          const payload = axiosError.response?.data;

          const messageText = payload?.message ?? axiosError.message;
          const statusCodeValue = getValidStatusCode(payload?.statusCode);

          const capturePayload: {
            message: string;
            statusCode?: number;
            raw: string;
          } = {
            message: messageText,
            raw: JSON.stringify(payload ?? axiosError.message),
          };

          if (statusCodeValue !== undefined) {
            capturePayload.statusCode = statusCodeValue;
          }

          adminCaptureException(capturePayload, (scope) => {
            scope.setTags({
              queryKey: PRODUCT_REWARDS_QUERY_KEY,
              status_code: payload?.statusCode,
            });
            scope.setUser({
              id: session.userId,
              email: session.userEmail,
            });
            scope.setContext('error', {
              error: payload ?? axiosError.toJSON?.() ?? axiosError.message,
            });
            scope.setLevel('error');
          });
        }

        throw error;
      }
    },
    enabled: Boolean(orderId && session.access_token),
    select: (data) => data ?? { promo_rewards: [], voucher_rewards: [] },
  });
};
